import { FC, ReactNode, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Tabs,
  Tab,
  Box,
  Badge,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useIsEntityVp } from "../../../auth/entityVpContext";
import { canOverrideLifecycleProgress } from "../../../auth/permissions";
import { useRoles } from "../../../auth/useRoles";
import { DialogFooterActions } from "../dialogFooterActions";
import { ProgressOverride, ProgressOverrideMenu } from "../progressOverride";
import { AttachmentUploader } from "../attachmentUploader";
import { AttachmentList } from "../attachmentsList";
import { LifecycleAttachmentApi } from "../types";

type FooterActions = {
  onClose: () => void;
  onSave: () => void;
  onComplete: () => void;
  canComplete: boolean;
  isSaving?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  attachmentApi: LifecycleAttachmentApi;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  headerContent?: ReactNode;
  children: ReactNode;
  actions: FooterActions;
  /** Shown only to Admin and Market Access, see canOverrideLifecycleProgress. */
  progressOverride?: ProgressOverride;
};

export const LifecycleDialog: FC<Props> = ({
  open,
  onClose,
  title,
  attachmentApi,
  maxWidth = "sm",
  fullWidth = true,
  headerContent,
  children,
  actions,
  progressOverride
}) => {
  const [tab, setTab] = useState(0);
  const entityId = useParams<{ id: string }>().id!;
  const lifecycleName = title.trim().toLowerCase().replace(/\s+/g, "");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // A VP reads the lifecycle but gets neither the attachments tab nor the write actions.
  // Read from context rather than taking a prop, so none of the 12 call sites change.
  const isEntityVp = useIsEntityVp();
  const showAttachments = !isEntityVp;
  const roles = useRoles();
  const showProgressOverride =
    !!progressOverride && !isEntityVp && canOverrideLifecycleProgress(roles);

  // The attachment api methods are hooks, so this stays unconditional even when the tab that
  // uses it is hidden. See LifecycleAttachmentApi in ../types.
  const { data: attachments = [] } = attachmentApi.useList(
    entityId,
    lifecycleName
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isMobile}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 3
        }}
        textColor="secondary"
        indicatorColor="secondary"
      >
        <Tab label="Details" value={0} />
        {showAttachments && (
          <Tab
            value={1}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Attachments
                {attachments.length > 0 && (
                  <Badge
                    badgeContent={attachments.length}
                    color="success"
                    sx={{
                      "& .MuiBadge-badge": {
                        position: "static",
                        transform: "none"
                      }
                    }}
                  />
                )}
              </Box>
            }
          />
        )}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <DialogTitle sx={{ position: "relative" }}>
          <Typography variant="h5" component="span">
            {title}
          </Typography>
          {headerContent}
          {showProgressOverride && (
            <Box sx={{ position: "absolute", top: 12, right: 12 }}>
              <ProgressOverrideMenu override={progressOverride} />
            </Box>
          )}
        </DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogFooterActions
          title={title}
          onClose={actions.onClose}
          onSave={actions.onSave}
          onComplete={actions.onComplete}
          canComplete={actions.canComplete}
          isSaving={actions.isSaving}
          readOnly={isEntityVp}
        />
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <DialogContent sx={{ pt: 3 }}>
          <AttachmentUploader
            entityId={entityId}
            lifecycleName={lifecycleName}
            attachmentApi={attachmentApi}
          />
          <AttachmentList
            entityId={entityId}
            lifecycleName={lifecycleName}
            attachments={attachments}
            attachmentApi={attachmentApi}
          />
        </DialogContent>
        <DialogFooterActions
          title={title}
          onClose={actions.onClose}
          onSave={actions.onSave}
          onComplete={actions.onComplete}
          canComplete={actions.canComplete}
          isSaving={actions.isSaving}
        />
      </TabPanel>
    </Dialog>
  );
};

type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
};

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`lifecycle-tabpanel-${index}`}
    aria-labelledby={`lifecycle-tab-${index}`}
  >
    {value === index && children}
  </div>
);
